import { __awaiter } from 'tslib';
import { IdleValue, Priority } from '@tencent/idle-loader';
import { getClass, nameOf, Metadata, Registry } from '@tencent/workbench-core';
import { ProxyProviderRegistry, ProviderRegistry } from './provider.js';
import { Container } from './container.js';
import { IInstantiationHookPoint } from '@tencent/containerized';
import { DynamicLoader, DynamicLoaderRegistry } from './dynamic-loader.js';
import { PostConstructorRegistry } from './post-constructor.js';
import { NOT_REGISTERED, generatePostConstructorError } from '../contants/error-msgs.js';

const IS_SUPPORT_PROXY = (() => {
    if (typeof Proxy !== 'function')
        return false;
    return Proxy.toString().includes('[native code]');
})();
/**
 * 用于管理所有的可注入的类，作为依赖注入的容器
 * 较为底层，不建议直接使用此类获取实例。
 *
 * @public
 */
class InjectorService extends ProxyProviderRegistry {
    static getServices(locals, designParamTypes, origin) {
        const services = designParamTypes
            .map((serviceType, index) => {
            const serviceName = typeof serviceType === 'function' ? nameOf(serviceType) : serviceType;
            if (locals.has(serviceName)) {
                return locals.get(serviceName);
            }
            if (locals.has(serviceType)) {
                return locals.get(serviceType);
            }
            if (this.has(serviceType)) {
                return this.get(serviceType);
            }
            if (origin) {
                throw new Error(`${NOT_REGISTERED}, the ${index} param requested by${String(origin)} `);
            }
            return undefined;
        });
        return services;
    }
    static withContainer(outsideContainer) {
        this.container = new Container();
        this.container.adoptBy(outsideContainer);
        ProviderRegistry.forEach((provider, key) => {
            var _a;
            // 两个di用的key应该保持一致
            this.container.set(key, provider);
            this.registerPostConstructorInContainer(key, this.container);
            (_a = this.container) === null || _a === void 0 ? void 0 : _a.invokable(provider.useClass);
        });
        this.aliases.forEach((value, key) => {
            const provider = ProviderRegistry.get(value);
            if (provider) {
                this.container.set(key, provider);
            }
            else {
                console.error('cannot find alias\'s corresponding provider in ProviderRegistry');
            }
        });
    }
    static registerPostConstructorInContainer(key, container) {
        const callbacks = PostConstructorRegistry.get(key);
        callbacks === null || callbacks === void 0 ? void 0 : callbacks.forEach((callback) => {
            container.addHook(key, IInstantiationHookPoint.POST_CONSTRUCTOR, callback);
        });
    }
    static invoke(useClass, locals = new Map(), designParamTypes) {
        if (!designParamTypes) {
            designParamTypes = Metadata.getParamTypes(useClass);
        }
        const services = this.getServices(locals, designParamTypes, useClass);
        return new useClass(...services);
    }
    static invokeMethod(handler, options) {
        let designParamTypes;
        let target;
        let methodName;
        let locals = new Map();
        if (options instanceof Array) {
            designParamTypes = options;
        }
        else {
            designParamTypes = options.designParamTypes;
            target = options.target;
            methodName = options.methodName;
            if (options.locals) {
                locals = options.locals;
            }
        }
        if (locals instanceof Map === false) {
            locals = new Map();
        }
        if (handler.$injected) {
            return handler.call(target, locals);
        }
        if (!designParamTypes) {
            designParamTypes = Metadata.getParamTypes(target, methodName);
        }
        const services = this.getServices(locals, designParamTypes);
        return handler(...services);
    }
    static invokeGetter(target, targetKey) {
        const designType = Metadata.getType(target, targetKey);
        if (!this.has(designType)) {
            return undefined;
            // const serviceName = typeof designType === 'function' ? nameOf(designType) : designType;
            // throw new InjectionError(target, serviceName.toString());
        }
        return this.get(designType);
    }
    static invokeProxy(target, locals = new Map(), designParamTypes) {
        if (!IS_SUPPORT_PROXY) {
            // eager instantiation
            return InjectorService.invoke(target, locals, designParamTypes);
        }
        // Return a proxy object that's backed by an idle value. That
        // strategy is to instantiate instance in our idle time or when actually
        // needed but not when injected into a consumer
        const idle = new IdleValue(() => InjectorService.invoke(target, locals, designParamTypes), { priority: Priority.normal });
        return new Proxy(Object.create(null), {
            get(instance, key) {
                if (key in instance) {
                    return instance[key];
                }
                // don't new instance when read constructor
                if (key === 'constructor') {
                    return target.constructor;
                }
                // don't new instance when read prototype
                if (key === 'prototype') {
                    return target.prototype;
                }
                // now we must get the real obj
                const obj = idle.getValue();
                let prop = obj[key];
                if (typeof prop !== 'function') {
                    return prop;
                }
                prop = prop.bind(obj);
                instance[key] = prop;
                return prop;
            },
            set(instance, p, value) {
                instance[p] = value;
                const obj = idle.getValue();
                obj[p] = value;
                return true;
            },
            defineProperty(instance, p, attributes) {
                const obj = idle.getValue();
                Object.defineProperty(obj, p, attributes);
                return true;
            },
            getPrototypeOf() {
                return target.prototype;
            },
        });
    }
    static set(provider, instance) {
        var _a;
        let target;
        if (provider.provide === undefined) {
            target = provider;
            provider = {
                provide: target,
                useClass: target,
                instance,
            };
        }
        else {
            target = provider.provide;
        }
        let typeKey = provider.useClass;
        // FIXME: DynamicLoader 兼容，需要 useValue 的支持
        if (provider.useClass === DynamicLoader) {
            class P extends DynamicLoader {
            }
            typeKey = P;
        }
        ProviderRegistry.merge(typeKey, provider);
        if (this.container) {
            this.container.set(typeKey, provider);
        }
        if (typeKey && target !== typeKey) {
            this.alias(target, typeKey);
        }
        if (DynamicLoaderRegistry.has(provider.provide)) {
            const loader = DynamicLoaderRegistry.get(provider.provide);
            loader.loaded = true;
            (_a = loader.onModuleLoad) === null || _a === void 0 ? void 0 : _a.call(loader, provider.useClass);
        }
        return InjectorService;
    }
    static get(target) {
        var _a;
        const key = (_a = this.aliases.get(target)) !== null && _a !== void 0 ? _a : target;
        const provider = ProviderRegistry.get(key);
        if (!this.has(target)) {
            throw new Error(`${NOT_REGISTERED}:${String(target)}`);
        }
        let instance = provider === null || provider === void 0 ? void 0 : provider.instance;
        if (instance) {
            return instance;
        }
        // 有 containerized 使用containerized
        if (this.container) {
            instance = this.container.get(key);
            if (provider) {
                provider.instance = instance;
            }
            return instance;
        }
        if (provider) {
            provider.instance = InjectorService.invoke(provider.useClass);
            InjectorService.runPostConstructors(target, provider);
            instance = provider.instance;
            return instance;
        }
        return instance;
    }
    static runPostConstructors(target, provider) {
        const callbacks = PostConstructorRegistry.get(target);
        if (!callbacks) {
            return;
        }
        callbacks.forEach((callback) => {
            try {
                callback(provider.instance);
            }
            catch (e) {
                console.error(generatePostConstructorError(provider.useClass, target, e.message));
                console.error(e);
            }
        });
    }
    static has(target) {
        var _a, _b;
        const key = (_a = this.aliases.get(target)) !== null && _a !== void 0 ? _a : target;
        const containerHasKey = (_b = this.container) === null || _b === void 0 ? void 0 : _b.has(key);
        return containerHasKey !== null && containerHasKey !== void 0 ? containerHasKey : ProviderRegistry.has(key);
    }
    static alias(key, target) {
        this.aliases.set(key, getClass(target));
        const provider = ProviderRegistry.get(getClass(target));
        if (!provider) {
            console.error('cannot find alias\'s corresponding provider in ProviderRegistry');
            return;
        }
        if (this.container) {
            this.container.set(key, provider);
        }
    }
    static construct(target) {
        var _a;
        const key = (_a = this.aliases.get(target)) !== null && _a !== void 0 ? _a : target;
        const provider = ProviderRegistry.get(key);
        return this.invoke(provider.useClass);
    }
    /**
     * 替换对应key为新的instance，和bind不同点在其可以替换instance
     * containerized不支持有实例后替换instance
     * override要求原key存在
     * @param original - 要被覆盖的key
     * @param target - 覆盖的class
     */
    static override(original, target) {
        var _a, _b;
        const key = (_a = this.aliases.get(original)) !== null && _a !== void 0 ? _a : original;
        const originalProvider = ProviderRegistry.get(key);
        if (!originalProvider) {
            console.error('Original class has not been registered');
            return;
        }
        if (!originalProvider.overridable) {
            console.warn('Original class cannot be overrided');
            return;
        }
        originalProvider.useClass = target;
        originalProvider.instance = undefined;
        if (originalProvider.overridable) {
            (_b = this.container) === null || _b === void 0 ? void 0 : _b.set(key, originalProvider);
        }
    }
    static hasClass(target) {
        var _a;
        const key = (_a = this.aliases.get(target)) !== null && _a !== void 0 ? _a : target;
        if (ProviderRegistry.get(key)) {
            return true;
        }
        return false;
    }
    static getClass(target) {
        var _a;
        const key = (_a = this.aliases.get(target)) !== null && _a !== void 0 ? _a : target;
        return ProviderRegistry.get(key).useClass;
    }
    static invokable(target) {
        // 针对 containerized DI 框架进行支持，若无 containerized 则已自动支持 invokable
        if (this.container) {
            this.container.invokable(target);
        }
    }
    static emit(eventName, ...args) {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = [];
            ProviderRegistry.forEach((provider) => {
                if (!provider.instance)
                    return;
                const service = InjectorService.get(provider.provide);
                if (service && (eventName in service)) {
                    try {
                        promises.push(service[eventName](...args));
                    }
                    catch (e) {
                        console.error(e);
                    }
                }
            });
            return Promise.all(promises);
        });
    }
    static emitOn(eventName, targets, ...args) {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = [];
            let registry = ProviderRegistry;
            let callback = (_p) => true;
            if (targets instanceof Registry) {
                registry = targets;
            }
            else {
                callback = targets;
            }
            registry.forEach((provider, providerKey) => {
                if (!callback(provider))
                    return;
                if (!provider.instance)
                    return;
                const service = InjectorService.get(providerKey);
                if (!(service instanceof provider.useClass))
                    return;
                if (service && (eventName in service)) {
                    try {
                        promises.push(service[eventName](...args));
                    }
                    catch (e) {
                        console.error(e);
                    }
                }
            });
            return Promise.all(promises);
        });
    }
    static emitAt(eventName, token, ...args) {
        return __awaiter(this, void 0, void 0, function* () {
            const service = InjectorService.get(token);
            if (service && (eventName in service)) {
                try {
                    return new Promise((resolve) => {
                        const result = service[eventName](...args);
                        resolve(result);
                    });
                }
                catch (e) {
                    console.error(e);
                }
            }
            return Promise.resolve(null);
        });
    }
    static buildRegistry(registry, callback = p => p.instance === undefined) {
        registry.forEach((provider) => {
            if (callback(provider)) {
                if (this.container) {
                    provider.instance = this.container.get(provider.useClass);
                }
                else {
                    if (provider.supportDeferInvoke) {
                        provider.instance = InjectorService.invokeProxy(provider.useClass);
                    }
                    else {
                        provider.instance = InjectorService.invoke(provider.useClass);
                    }
                }
            }
        });
        return registry;
    }
    static service(...args) {
        const options = args[args.length - 1];
        const target = args[args.length - 2];
        let token = target;
        if (args.length === 3) {
            [token] = args;
        }
        InjectorService.set(Object.assign(Object.assign({ overridable: false }, options), { singleton: true, provide: token, useClass: target }));
    }
    static bind(token, target) {
        var _a;
        const targetProvider = ProviderRegistry.get(target);
        if (!targetProvider) {
            // target必须是injectable 不然找不到designParameter和useClass
            throw new Error('target should be injectable');
        }
        if (targetProvider.instance) {
            // containerize不支持实例的替换，以期避免和已经retained不一致
            throw new Error('target has been instantiated');
        }
        let originalProvider = ProviderRegistry.get(token);
        // TODO: token不存在，此时target为key,token为alias，无法再对token进行alias,set方法需要改
        if (!originalProvider) {
            InjectorService.set({
                provide: token,
                useClass: target,
                overridable: true,
            });
            originalProvider = ((_a = ProviderRegistry.get(token)) !== null && _a !== void 0 ? _a : ProviderRegistry.get(target));
        }
        if (originalProvider.instance) {
            // containerize不支持实例的替换，以期避免和已经retained不一致
            throw new Error('token\'s target has been instantiated');
        }
        if (!originalProvider.overridable && this.container) {
            throw new Error('containerized requires provider be overridable in order to bind');
        }
        originalProvider.useClass = target;
        if (this.container) {
            // TODO: 处理metadata， 现在withContainer得比bind后调用
            this.container.set(token, originalProvider);
        }
        return this;
    }
    /**
     * 对于token注册一个回调，将在构造之后执行
     * @public
     */
    static addPostConstructor(token, callback) {
        PostConstructorRegistry.setCallback(token, callback);
        if (this.container) {
            this.container.addHook(token, IInstantiationHookPoint.POST_CONSTRUCTOR, callback);
        }
    }
    // ------------------------------------------------
    // |               INSTANCE METHODS               |
    // ------------------------------------------------
    invoke(target, locals = new Map(), designParamTypes) {
        return InjectorService.invoke(target, locals, designParamTypes);
    }
    invokeMethod(target, options) {
        return InjectorService.invokeMethod(target, options);
    }
    invokeGetter(target, targetKey) {
        return InjectorService.invokeGetter(target, targetKey);
    }
    get(target) {
        return InjectorService.get(target);
    }
    set(target, provider) {
        InjectorService.set(provider);
        return this;
    }
    has(target) {
        return InjectorService.has(target);
    }
    construct(target) {
        return InjectorService.construct(target);
    }
    override(original, target) {
        InjectorService.override(original, target);
    }
    emit(eventName, ...args) {
        return InjectorService.emit(eventName, ...args);
    }
    emitOn(eventName, targets, ...args) {
        return InjectorService.emitOn(eventName, targets, ...args);
    }
    emitAt(eventName, token, ...args) {
        return InjectorService.emitAt(eventName, token, ...args);
    }
}
InjectorService.aliases = new Map();
InjectorService.container = null;
InjectorService.factory = (token, instance) => InjectorService.set({
    provide: token,
    useClass: getClass(instance),
    instance,
    singleton: true,
});
InjectorService.factory(InjectorService, new InjectorService());

export { InjectorService };
//# sourceMappingURL=injector-service.js.map
